import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { arrow, autoUpdate, flip, offset, safePolygon, shift, useClick, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole, } from "@floating-ui/react";
import React, { useRef, useState, useImperativeHandle } from "react";
import { mergeRefs } from "react-merge-refs";
import { bug, unreachable } from "./err";
import { useAppkitConfig } from "./config";
const FloatingContext = React.createContext(null);
const useFloatingContext = () => {
    const context = React.useContext(FloatingContext);
    if (context == null) {
        return bug("Missing <WithFloating> context");
    }
    return context;
};
/**
 * Provides a context for floating elements. Has to surround the floating
 * element and its reference.
 *
 * Some styles of the floating element need to be specified as props of this
 * container as they influence positioning.
 */
export const FloatingContainer = React.forwardRef(({ children, placement: idealPlacement, arrowSize = 8, distance = 4, borderRadius = 4, viewPortMargin = 8, ariaRole = "tooltip", onClose = () => { }, className, ...rest }, ref) => {
    const [open, setOpen] = useState(false);
    const actualOpen = "open" in rest ? rest.open : open;
    const arrowRef = useRef(null);
    const div = useRef(null);
    useImperativeHandle(ref, () => Object.assign(div.current ?? unreachable(), {
        open: () => setOpen(true),
        close: () => setOpen(false),
    }));
    // Setup positioning
    const { x, y, placement, refs, middlewareData, context: floatContext, } = useFloating({
        open: actualOpen,
        onOpenChange: open => {
            if (!("open" in rest)) {
                setOpen(open);
            }
            if (!open) {
                onClose();
            }
        },
        placement: idealPlacement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(arrowSize + distance),
            flip(),
            shift({ padding: viewPortMargin }),
            arrow({
                element: arrowRef,
                // In theory we have to multiply by √2 = 1.41... here, but we
                // also want some extra padding so that the arrow doesn't touch
                // the rounded corner directly. Factor 1.6 works well.
                padding: borderRadius * 1.6,
            }),
        ],
    });
    // Setup interactions
    const hover = useHover(floatContext, {
        enabled: "trigger" in rest && rest.trigger === "hover",
        handleClose: safePolygon(),
        move: false,
    });
    const focus = useFocus(floatContext, {
        enabled: "trigger" in rest && rest.trigger === "hover",
    });
    const click = useClick(floatContext, {
        enabled: "trigger" in rest && rest.trigger === "click",
    });
    const dismiss = useDismiss(floatContext, {
        referencePress: ariaRole === "tooltip",
    });
    const role = useRole(floatContext, { role: ariaRole });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss, role]);
    // Setup context
    const context = {
        open: actualOpen,
        setOpen: ariaRole === "tooltip" ? setOpen : null,
        settings: { arrowSize, distance, borderRadius, viewPortMargin },
        calculated: { x, y, placement, arrow: middlewareData.arrow },
        refs: { arrowRef, ...refs },
        getReferenceProps,
        getFloatingProps,
    };
    return (_jsx(FloatingContext.Provider, { value: context, children: _jsx("div", { ref: div, css: { position: "relative" }, className, children: children }) }));
});
/**
 * Component to designate its single child element as "trigger" a floating
 * element, i.e. the reference element. Has to be placed inside a
 * `<FloatingContainer>`. The child of this component has to properly deal with
 * a `ref` prop (i.e. use `forwardRef` when using function components). It also
 * needs to accept and forward all props of the inner node (e.g. `div`), as
 * some props like `onClick` are crucial for this to work.
 */
export const FloatingTrigger = ({ children }) => {
    const context = useFloatingContext();
    return React.cloneElement(children, {
        "data-floating-state": context.open ? "open" : "closed",
        ...context.getReferenceProps({
            ref: context.refs.setReference,
            onClick: () => context.open && context.setOpen?.(false),
            ...children.props,
        }),
    });
};
/**
 * A floating element (e.g. a tooltip or popover).
 *
 * Has to be placed inside a `<FloatingContainer>` together with a
 * `<FloatingTrigger>`.
 */
export const Floating = React.forwardRef(({ children, backgroundColor, borderColor, borderWidth = 1, shadowColor = "rgba(0, 0, 0, 20%)", shadowBlur = 4, padding = [4, 8], className, hideArrowTip = false, }, ref) => {
    const config = useAppkitConfig();
    const { open, calculated, refs, settings, ...context } = useFloatingContext();
    // Render nothing if the floating element is not opened.
    if (!open) {
        return null;
    }
    const pos = sideOfPlacement(calculated.placement);
    const arrowSideLen = Math.SQRT2 * settings.arrowSize;
    const mergedRefs = mergeRefs([ref, refs.setFloating]);
    return (_jsxs("div", { ...context.getFloatingProps({ ref: mergedRefs }), css: {
            "--floating-background-color": backgroundColor ?? config.colors.neutral05,
            "--floating-border-color": borderColor ?? config.colors.neutral40,
            "--floating-border-width": `${borderWidth}px`,
            "--floating-shadow-blur": `${shadowBlur}px`,
            "--floating-shadow-color": shadowColor,
            // Positioning
            position: "absolute",
            ...calculated.x != null && calculated.y != null
                ? { left: calculated.x, top: calculated.y }
                : { [invSide(pos)]: "100%" },
            width: "max-content",
            maxWidth: `calc(100vw - ${2 * settings.viewPortMargin}px)`,
            zIndex: 10000,
            // Styling
            backgroundColor: "var(--floating-background-color)",
            borderRadius: settings.borderRadius,
            boxShadow: "0 0 var(--floating-shadow-blur) var(--floating-shadow-color)",
            outline: "var(--floating-border-width) solid var(--floating-border-color)",
        }, children: [_jsx("div", { className, css: {
                    // TODO: sometimes you might want to not hide overflow
                    overflow: "hidden",
                    borderRadius: settings.borderRadius,
                    padding: (Array.isArray(padding) ? padding : [padding])
                        .map(n => `${n}px`)
                        .join(" "),
                }, children: children }), _jsx("div", { css: {
                    position: "absolute",
                    display: hideArrowTip ? "none" : "block",
                    ...pos === "top" || pos === "bottom"
                        ? {
                            left: 0,
                            right: 0,
                            height: settings.arrowSize + shadowBlur,
                        }
                        : {
                            top: 0,
                            bottom: 0,
                            width: settings.arrowSize + shadowBlur,
                        },
                    [pos]: "100%",
                    pointerEvents: "none",
                    overflow: "hidden",
                }, children: _jsx("div", { ref: refs.arrowRef, css: {
                        // Positioning
                        position: "absolute",
                        left: calculated.arrow?.x,
                        top: calculated.arrow?.y,
                        [pos]: -arrowSideLen / 2,
                        height: arrowSideLen,
                        width: arrowSideLen,
                        transform: "rotate(45deg)",
                        // Styling
                        backgroundColor: "var(--floating-background-color)",
                        boxShadow: `${shadowBlur / 6}px ${shadowBlur / 6}px `
                            + "var(--floating-shadow-blur) "
                            + `${shadowBlur / 12}px `
                            + "var(--floating-shadow-color)",
                        outline: "var(--floating-border-width) solid var(--floating-border-color)",
                    } }) })] }));
});
/** Adds an on-hover tooltip to the given `children`. */
export const WithTooltip = React.forwardRef(({ children, tooltip, tooltipCss, ...props }, ref) => {
    const config = useAppkitConfig();
    return (_jsxs(FloatingContainer, { ref: ref, ...props, trigger: "hover", placement: props.placement ?? "top", children: [_jsx(Floating, { css: {
                    color: config.colors.neutral80,
                    fontSize: 14,
                    maxWidth: "100%",
                    ...tooltipCss,
                }, children: tooltip }), _jsx(FloatingTrigger, { children: children })] }));
});
// ===== Utilities ===============================================================================
const invSide = (pos) => {
    const map = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
    };
    return map[pos];
};
const sideOfPlacement = (placement) => placement.split("-")[0];
//# sourceMappingURL=floating.js.map